/**
 * Design tokens — color, spacing, radius, shadows, typography scale, z-index.
 *
 * Use these instead of inline `8px`, `1rem`, `2em`, `0px 8px 16px rgba(0,0,0,0.05)`
 * etc. The values codify the 4/8-multiple spacing system Alfred mostly already
 * uses (audit found 8/16/24/32 most common; this file picks them as canonical).
 *
 *   import { color, space, radius, shadow } from 'app/theme/tokens'
 *   styled.div`padding: ${space.md}; border-radius: ${radius.md};`
 */

// Brand + UI colors. This file carried NO colors until 2026-09-08, so every
// component hand-picked its own hex and the palette drifted — the case that
// prompted this was a "Declined subscription" chip on the parent profile in an
// invented green (#2E7D5B on #f0f7f3) that matched nothing else on the screen.
//
// `brand`/`cream`/`ink` are the OpenHouse brand three. Everything else is
// derived for UI use. ⛔ Do not add a green/blue "accent" here to make one
// component look nicer — if a new hue is genuinely needed it is a brand
// decision, not a component decision.
export const color = {
  // Brand
  brand: '#F25E35',        // OpenHouse orange — primary actions, active nav
  brandSoft: '#FDEAE4',    // tinted orange for icon tiles and soft fills
  cream: '#FBF5ED',        // page ground behind the white cards
  ink: '#282E3C',          // navy — primary text

  // Neutrals
  surface: '#FFFFFF',      // card background
  border: '#E8E2D9',       // hairline on cream; softer than MUI's default grey
  textMuted: '#7A7A7A',    // the uppercase micro-labels on the profile cards
  textFaint: '#9E9E9E',    // placeholders, em-dashes, "never"

  // Status — used by the audit boards. Reserve these for true state, and note
  // that `positive` reads as "this is good": do not use it for a neutral fact
  // like a parent declining, which is what the chip above got wrong.
  positive: '#2E7D32',
  warning: '#EF6C00',
  negative: '#C62828'
}

// Spacing scale — multiples of 4px. Names map to typical UI density steps.
export const space = {
  none: '0',
  xxs: '2px',
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  xxxl: '64px'
}

// Border radius scale. `pill` is for fully-rounded buttons/chips.
export const radius = {
  none: '0',
  sm: '4px',
  md: '8px',
  lg: '16px',
  xl: '24px',
  pill: '999px'
}

// Box shadows. `default` matches the existing `oh-default-shadow`.
export const shadow = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.04)',
  md: '0 4px 8px rgba(0, 0, 0, 0.04)',
  default: '0px 8px 16px rgba(0, 0, 0, 0.05)',
  lg: '0 12px 24px rgba(0, 0, 0, 0.08)',
  modal: '0 16px 40px rgba(0, 0, 0, 0.16)'
}

// Z-index ladder so layered UI (drawer, modal, toast, dropdown) doesn't fight.
export const zIndex = {
  base: 0,
  sticky: 100,
  dropdown: 1000,
  drawer: 1100,
  modal: 1200,
  toast: 1300,
  tooltip: 1400
}

// Typography weights — codify the existing usage (400 regular, 600 semibold, 700 bold).
export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700
}

// Animation timing — used by transitions / tab indicators / hover states.
export const motion = {
  fast: '120ms ease-out',
  base: '200ms ease-out',
  slow: '320ms ease-in-out'
}

// Responsive breakpoints. Mobile-first: the bare numbers are minimum widths.
// `down(...)` / `up(...)` helpers return media-query strings for styled-components.
//
//   styled.div`
//     padding: ${space.md};
//     ${breakpoint.up('tablet')} { padding: ${space.lg}; }
//   `
//
// Values chosen to match MUI v4 defaults (xs<600, sm 600+, md 960+, lg 1280+)
// so this layer plays nicely with MUI Hidden / useMediaQuery.
export const breakpoint = {
  mobile: 0,
  tablet: 600,
  desktop: 960,
  wide: 1280,
  down: (key) => `@media (max-width: ${({
    mobile: 599,
    tablet: 959,
    desktop: 1279,
    wide: 9999
  })[key]}px)`,
  up: (key) => `@media (min-width: ${({
    mobile: 0,
    tablet: 600,
    desktop: 960,
    wide: 1280
  })[key]}px)`
}

// Touch-target / safe-area tokens — keep mobile chrome consistent.
export const mobile = {
  topBarHeight: '56px',     // standard Material app bar on mobile
  bottomNavHeight: '56px',  // standard Material bottom-nav
  tapTarget: '44px',        // iOS HIG / WCAG minimum
  // CSS env() vars for iOS notch / home-indicator. Fall back to 0 elsewhere.
  safeAreaTop: 'env(safe-area-inset-top, 0px)',
  safeAreaBottom: 'env(safe-area-inset-bottom, 0px)'
}
